import { AlertTriangle, Ban, Plus, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';
import { useActionsRessources, useEncaissements, usePaiementsQuotesParts, usePrevisionsRessources, useRessources, useSourcesRessources } from '@/api/hooks';
import { LIBELLE_BENEFICIAIRE, type BeneficiaireQuotePart, type PrevisionRessource, type SituationRessources } from '@/api/types';
import { useSession } from '@/auth/session';
import { BoutonExcel, Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateFr, montant, pct } from '@/lib/utils';

const r2 = (n: number) => Math.round(n * 100) / 100;
const compact = (n: number) => (Math.abs(n) >= 1_000_000 ? `${(n / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M` : Math.abs(n) >= 1_000 ? `${(n / 1_000).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} k` : n.toLocaleString('fr-FR'));
const BENEFICIAIRES: BeneficiaireQuotePart[] = ['CIA_VAR', 'ECOSYSTEME'];

/** Fiche d'une quote-part : due, payée, restant dû (lue comme une dette). */
function FicheQuotePart({ s, b }: { s: SituationRessources; b: BeneficiaireQuotePart }) {
  const q = s.quotesParts[b];
  const alertes = s.alertes.filter((a) => a.beneficiaire === b);
  return (
    <Card className={cn(alertes.length && 'border-orange-300')}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {q.libelle} <Badge variant="outline">{q.taux} % des revenus encaissés</Badge>
          {alertes.length > 0 && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="size-3" /> impayée à la clôture : {alertes.map((a) => `T${a.trimestre}`).join(', ')}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-xs uppercase text-muted-foreground">Due (cumul)</div>
          <div className="num font-semibold">{montant(q.due)}</div>
        </div>
        <div>
          <div className="text-xs uppercase text-muted-foreground">Payée</div>
          <div className="num font-semibold">{montant(q.payee)}</div>
        </div>
        <div>
          <div className="text-xs uppercase text-muted-foreground">Restant dû</div>
          <div className={cn('num font-semibold', q.restantDu > 0.5 && 'text-orange-700')}>{montant(q.restantDu)}</div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Grille des revenus prévus (source × trimestre) modifiable par la Finance. */
function GrillePrevisions({ exercice, previsions, sources, modifiable }: { exercice: number; previsions: PrevisionRessource[]; sources: string[]; modifiable: boolean }) {
  const actions = useActionsRessources();
  const toutes = [...new Set([...sources, ...previsions.map((p) => p.source)])];
  const initiales = () => Object.fromEntries(toutes.flatMap((src) => [1, 2, 3, 4].map((t) => [`${src}|${t}`, String(previsions.find((p) => p.source === src && p.trimestre === t)?.montant ?? '')])));
  const [valeurs, setValeurs] = useState<Record<string, string>>(initiales);
  const [nouvelleSource, setNouvelleSource] = useState('');
  const [sourcesAjoutees, setSourcesAjoutees] = useState<string[]>([]);
  useEffect(() => setValeurs(initiales()), [previsions, sources]); // eslint-disable-line react-hooks/exhaustive-deps
  const lignes = [...toutes, ...sourcesAjoutees.filter((s) => !toutes.includes(s))];
  const nombre = (v: string | undefined) => Number((v ?? '').replace(/\s/g, '').replace(',', '.')) || 0;
  const enregistrer = async () => {
    try {
      await actions.enregistrerPrevisions.mutateAsync({ exercice, lignes: lignes.flatMap((src) => [1, 2, 3, 4].map((t) => ({ trimestre: t, source: src, montant: nombre(valeurs[`${src}|${t}`]) }))) });
      toast.success('Revenus prévus enregistrés.');
      setSourcesAjoutees([]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Revenus prévus {exercice}</CardTitle>
        <CardDescription>Par source et par trimestre, en USD. {modifiable ? 'Un montant vide ou nul supprime la prévision.' : 'Saisie réservée à la Direction financière, au Chef comptable et à la trésorerie.'}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Source</TableHead>
                {[1, 2, 3, 4].map((t) => (
                  <TableHead key={t} className="text-right">
                    T{t}
                  </TableHead>
                ))}
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lignes.map((src) => (
                <TableRow key={src}>
                  <TableCell className="font-medium">{src}</TableCell>
                  {[1, 2, 3, 4].map((t) => (
                    <TableCell key={t} className="p-1 text-right">
                      {modifiable ? <Input className="h-8 w-32 text-right text-xs" inputMode="decimal" value={valeurs[`${src}|${t}`] ?? ''} onChange={(e) => setValeurs({ ...valeurs, [`${src}|${t}`]: e.target.value })} aria-label={`${src} T${t}`} /> : <span className="num text-xs">{montant(nombre(valeurs[`${src}|${t}`]))}</span>}
                    </TableCell>
                  ))}
                  <TableCell className="num text-right text-xs">{montant(r2([1, 2, 3, 4].reduce((s, t) => s + nombre(valeurs[`${src}|${t}`]), 0)))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {modifiable && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="nouvelle-source">Ajouter une source</Label>
              <Input id="nouvelle-source" value={nouvelleSource} onChange={(e) => setNouvelleSource(e.target.value)} placeholder="Produits financiers" maxLength={120} className="w-64" />
            </div>
            <Button variant="outline" onClick={() => { if (nouvelleSource.trim()) { setSourcesAjoutees([...sourcesAjoutees, nouvelleSource.trim()]); setNouvelleSource(''); } }} disabled={nouvelleSource.trim().length < 2}>
              <Plus className="size-4" /> Ajouter
            </Button>
            <Button onClick={enregistrer} disabled={actions.enregistrerPrevisions.isPending}>
              <Save className="size-4" /> Enregistrer les prévisions
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Ressources (revenus prévus et encaissés) et quotes-parts CIA-VAR / Écosystème (cahier des charges du tableau de bord, § 6 et 10). */
export function Ressources() {
  const session = useSession();
  const profils = session?.utilisateur.profils ?? [];
  const modifiable = profils.some((p) => ['DIRECTEUR_FINANCIER', 'CHEF_COMPTABLE', 'TRESORERIE', 'ADMINISTRATEUR'].includes(p));
  const [exercice, setExercice] = useState(new Date().getFullYear());
  const situation = useRessources(exercice);
  const sources = useSourcesRessources();
  const encaissements = useEncaissements(exercice);
  const previsions = usePrevisionsRessources(exercice);
  const paiements = usePaiementsQuotesParts(exercice);
  const actions = useActionsRessources();
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const [enc, setEnc] = useState({ date: aujourdhui, montant: '', source: '', reference: '', commentaire: '' });
  const [paie, setPaie] = useState({ beneficiaire: 'CIA_VAR' as BeneficiaireQuotePart, trimestre: Math.max(1, Math.ceil((new Date().getMonth() + 1) / 3) - 1), montant: '', date: aujourdhui, reference: '' });
  const nombre = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.')) || 0;
  const creerEncaissement = async () => {
    try {
      await actions.creerEncaissement.mutateAsync({ date: enc.date, montant: nombre(enc.montant), source: enc.source.trim(), reference: enc.reference.trim() || undefined, commentaire: enc.commentaire.trim() || undefined });
      toast.success('Encaissement enregistré.');
      setEnc({ ...enc, montant: '', reference: '', commentaire: '' });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };
  const creerPaiement = async () => {
    try {
      await actions.creerPaiement.mutateAsync({ beneficiaire: paie.beneficiaire, exercice, trimestre: paie.trimestre, montant: nombre(paie.montant), date: paie.date, reference: paie.reference.trim() || undefined });
      toast.success(`Paiement ${LIBELLE_BENEFICIAIRE[paie.beneficiaire]} · T${paie.trimestre} enregistré.`);
      setPaie({ ...paie, montant: '', reference: '' });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };
  const annuler = async (type: 'encaissement' | 'paiement', id: string) => {
    const motif = window.prompt("Motif de l'annulation (conservé dans le journal) :");
    if (!motif || motif.trim().length < 5) {
      if (motif !== null) toast.error('Le motif doit comporter au moins 5 caractères.');
      return;
    }
    try {
      if (type === 'encaissement') await actions.annulerEncaissement.mutateAsync({ id, motif: motif.trim() });
      else await actions.annulerPaiement.mutateAsync({ id, motif: motif.trim() });
      toast.success('Annulé ; la situation est recalculée.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Annulation impossible.');
    }
  };
  const s = situation.data;
  return (
    <div className="space-y-6">
      <PageHeader
        titre="Ressources et quotes-parts"
        sousTitre="Revenus prévus et encaissés par trimestre et par source, taux de couverture des dépenses, quotes-parts CIA-VAR et Écosystème dues sur les revenus encaissés : dues, payées, restant dû (lu comme une dette). Une quote-part impayée à la clôture d'un trimestre déclenche l'alerte A6."
        actions={
          <>
            <Input type="number" className="num w-24" min={2020} max={2100} value={exercice} onChange={(e) => setExercice(Number(e.target.value))} aria-label="Exercice" />
            <BoutonExcel chemin={`/ressources/export.xlsx?exercice=${exercice}`} nom={`FONAREV_Ressources_${exercice}.xlsx`} libelle="Excel" taille="default" />
          </>
        }
      />
      {situation.isLoading ? (
        <Chargement />
      ) : situation.error || !s ? (
        <ErreurBloc erreur={situation.error} />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Revenus encaissés" valeur={montant(s.totaux.encaisse)} detail={`${s.totaux.encaissements} encaissement(s) au ${dateFr(s.dateSituation)}`} />
            <Kpi label="Revenus prévus" valeur={montant(s.totaux.prevu)} detail={`encaissés / prévus : ${pct(s.totaux.tauxRealisation)}`} accent={s.totaux.tauxRealisation !== null && s.totaux.tauxRealisation < 70 ? 'alerte' : undefined} />
            <Kpi label="Taux de couverture" valeur={pct(s.totaux.tauxCouverture)} detail={`encaissés / payé cumulé (${montant(s.totaux.payeCumul)})`} accent={s.totaux.tauxCouverture !== null && s.totaux.tauxCouverture < 100 ? 'critique' : 'ok'} />
            <Kpi label="Quotes-parts restant dues" valeur={montant(r2(s.quotesParts.CIA_VAR.restantDu + s.quotesParts.ECOSYSTEME.restantDu))} detail={s.alertes.length ? `${s.alertes.length} alerte(s) A6` : 'aucune alerte'} accent={s.alertes.length ? 'critique' : 'ok'} />
          </div>
          {s.alertes.length > 0 && (
            <Card className="border-orange-300">
              <CardContent className="space-y-1 pt-4 text-sm">
                {s.alertes.map((a) => (
                  <div key={`${a.beneficiaire}-${a.trimestre}`} className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 size-4 text-orange-700" />
                    <span>
                      <Badge variant="outline">A6</Badge> {a.detail}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Revenus prévus et encaissés par trimestre</CardTitle>
                <CardDescription>Exercice {s.exercice} · en USD</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={s.trimestres.map((t) => ({ nom: t.libelle, Prévu: t.prevu, Encaissé: t.encaisse }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="nom" />
                      <YAxis tickFormatter={(v: number) => compact(v)} width={56} />
                      <Tooltip formatter={(v) => montant(Number(v ?? 0))} />
                      <Legend />
                      <Bar dataKey="Prévu" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Encaissé" fill="#1d4ed8" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <div className="space-y-4">
              {BENEFICIAIRES.map((b) => (
                <FicheQuotePart key={b} s={s} b={b} />
              ))}
            </div>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Situation par trimestre</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Trimestre</TableHead>
                    <TableHead className="text-right">Prévu</TableHead>
                    <TableHead className="text-right">Encaissé</TableHead>
                    <TableHead className="text-right">Réalisation</TableHead>
                    {BENEFICIAIRES.map((b) => (
                      <TableHead key={b} className="text-right">
                        {LIBELLE_BENEFICIAIRE[b]} due / payée / restant
                      </TableHead>
                    ))}
                    <TableHead>État</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {s.trimestres.map((t) => (
                    <TableRow key={t.trimestre} className={cn(t.alerte && 'bg-orange-50')}>
                      <TableCell className="font-medium">{t.libelle}</TableCell>
                      <TableCell className="num text-right">{montant(t.prevu)}</TableCell>
                      <TableCell className="num text-right">{montant(t.encaisse)}</TableCell>
                      <TableCell className="num text-right">{pct(t.tauxRealisation)}</TableCell>
                      {BENEFICIAIRES.map((b) => (
                        <TableCell key={b} className="num whitespace-nowrap text-right text-xs">
                          {montant(t.quotesParts[b].due)} / {montant(t.quotesParts[b].payee)} / <span className={cn(t.quotesParts[b].restantDu > 0.5 && 'font-semibold text-orange-700')}>{montant(t.quotesParts[b].restantDu)}</span>
                        </TableCell>
                      ))}
                      <TableCell>{t.alerte ? <Badge variant="destructive">quote-part impayée</Badge> : t.cloture ? <Badge variant="outline">clôturé</Badge> : <Badge variant="secondary">en cours</Badge>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          {previsions.data && <GrillePrevisions exercice={exercice} previsions={previsions.data} sources={sources.data?.sources ?? []} modifiable={modifiable} />}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Revenus encaissés</CardTitle>
                <CardDescription>Date, montant, source et référence de l'avis de crédit ; une saisie erronée s'annule avec motif, jamais supprimée.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {modifiable && (
                  <div className="grid gap-2 rounded-md border bg-muted/30 p-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="enc-date">Date</Label>
                      <Input id="enc-date" type="date" value={enc.date} onChange={(e) => setEnc({ ...enc, date: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="enc-montant">Montant (USD)</Label>
                      <Input id="enc-montant" inputMode="decimal" value={enc.montant} onChange={(e) => setEnc({ ...enc, montant: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="enc-source">Source</Label>
                      <Input id="enc-source" list="sources-revenus" value={enc.source} onChange={(e) => setEnc({ ...enc, source: e.target.value })} placeholder="Dotation de l’État" maxLength={120} />
                      <datalist id="sources-revenus">{(sources.data?.sources ?? []).map((x) => <option key={x} value={x} />)}</datalist>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="enc-reference">Référence</Label>
                      <Input id="enc-reference" value={enc.reference} onChange={(e) => setEnc({ ...enc, reference: e.target.value })} placeholder="AC-2026-031" maxLength={80} />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label htmlFor="enc-commentaire">Commentaire</Label>
                      <Input id="enc-commentaire" value={enc.commentaire} onChange={(e) => setEnc({ ...enc, commentaire: e.target.value })} maxLength={500} />
                    </div>
                    <div className="sm:col-span-2">
                      <Button onClick={creerEncaissement} disabled={actions.creerEncaissement.isPending || nombre(enc.montant) <= 0 || enc.source.trim().length < 2 || !enc.date}>
                        <Plus className="size-4" /> Enregistrer l'encaissement
                      </Button>
                    </div>
                  </div>
                )}
                {encaissements.isLoading ? (
                  <Chargement />
                ) : !encaissements.data?.length ? (
                  <Vide message="Aucun encaissement sur l'exercice." />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                        <TableHead className="hidden md:table-cell">Référence</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {encaissements.data.map((e) => (
                        <TableRow key={e.id} className={cn(e.annule && 'text-muted-foreground line-through')}>
                          <TableCell>{dateFr(e.date)}</TableCell>
                          <TableCell>
                            {e.source}
                            {e.origine === 'INTEGRATION' && (
                              <Badge variant="outline" className="ml-2 border-sky-200 bg-sky-50 text-sky-800" title="Transmis par l'application de collecte des revenus">
                                Collecte
                              </Badge>
                            )}
                            {e.annule && <div className="text-xs no-underline">Annulé : {e.motifAnnulation}</div>}
                          </TableCell>
                          <TableCell className="num text-right">{montant(e.montant)}</TableCell>
                          <TableCell className="hidden text-xs md:table-cell">{e.reference ?? ''}</TableCell>
                          <TableCell>
                            {modifiable && !e.annule && (
                              <Button size="icon" variant="ghost" onClick={() => annuler('encaissement', e.id)} title="Annuler avec motif" aria-label={`Annuler l'encaissement du ${dateFr(e.date)}`}>
                                <Ban className="size-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Paiements des quotes-parts</CardTitle>
                <CardDescription>Reversements CIA-VAR ({s.taux.CIA_VAR} %) et Écosystème ({s.taux.ECOSYSTEME} %) au titre d'un trimestre.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {modifiable && (
                  <div className="grid gap-2 rounded-md border bg-muted/30 p-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="qp-beneficiaire">Bénéficiaire</Label>
                      <SelectNatif id="qp-beneficiaire" value={paie.beneficiaire} onChange={(e) => setPaie({ ...paie, beneficiaire: e.target.value as BeneficiaireQuotePart })}>
                        {BENEFICIAIRES.map((b) => (
                          <option key={b} value={b}>
                            {LIBELLE_BENEFICIAIRE[b]}
                          </option>
                        ))}
                      </SelectNatif>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="qp-trimestre">Trimestre</Label>
                      <SelectNatif id="qp-trimestre" value={paie.trimestre} onChange={(e) => setPaie({ ...paie, trimestre: Number(e.target.value) })}>
                        {[1, 2, 3, 4].map((t) => (
                          <option key={t} value={t}>
                            T{t} {exercice} · dû {montant(s.trimestres[t - 1]?.quotesParts[paie.beneficiaire].due ?? 0)}
                          </option>
                        ))}
                      </SelectNatif>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="qp-montant">Montant (USD)</Label>
                      <Input id="qp-montant" inputMode="decimal" value={paie.montant} onChange={(e) => setPaie({ ...paie, montant: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="qp-date">Date</Label>
                      <Input id="qp-date" type="date" value={paie.date} onChange={(e) => setPaie({ ...paie, date: e.target.value })} />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label htmlFor="qp-reference">Référence</Label>
                      <Input id="qp-reference" value={paie.reference} onChange={(e) => setPaie({ ...paie, reference: e.target.value })} placeholder="OV-2026-118" maxLength={80} />
                    </div>
                    <div className="sm:col-span-2">
                      <Button onClick={creerPaiement} disabled={actions.creerPaiement.isPending || nombre(paie.montant) <= 0 || !paie.date}>
                        <Plus className="size-4" /> Enregistrer le paiement
                      </Button>
                    </div>
                  </div>
                )}
                {paiements.isLoading ? (
                  <Chargement />
                ) : !paiements.data?.length ? (
                  <Vide message="Aucun paiement de quote-part sur l'exercice." />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Bénéficiaire</TableHead>
                        <TableHead>Trimestre</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paiements.data.map((p) => (
                        <TableRow key={p.id} className={cn(p.annule && 'text-muted-foreground line-through')}>
                          <TableCell>
                            {p.libelleBeneficiaire}
                            {p.annule && <div className="text-xs">Annulé : {p.motifAnnulation}</div>}
                          </TableCell>
                          <TableCell>T{p.trimestre}</TableCell>
                          <TableCell>{dateFr(p.date)}</TableCell>
                          <TableCell className="num text-right">{montant(p.montant)}</TableCell>
                          <TableCell>
                            {modifiable && !p.annule && (
                              <Button size="icon" variant="ghost" onClick={() => annuler('paiement', p.id)} title="Annuler avec motif" aria-label={`Annuler le paiement ${p.libelleBeneficiaire} T${p.trimestre}`}>
                                <Ban className="size-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
